/**
 * Mock User Data
 *
 * Comprehensive mock user database for admin user management interfaces.
 * Includes users across all roles: COMPANY, CONSULTANT, AUDITOR, ADMIN
 */

export type UserRole = 'COMPANY' | 'CONSULTANT' | 'AUDITOR' | 'ADMIN';
export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';

export interface MockUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: UserRole;
  organization: string;
  status: UserStatus;
  registrationDate: string;
  lastLogin: string;
  permissions: string[];
  activityLog: ActivityLogEntry[];
  associatedRecords: {
    applications: number;
    documents: number;
    payments: number;
    clients?: number;
    assignedAudits?: number;
  };
}

export interface ActivityLogEntry {
  timestamp: string;
  action: string;
  details: string;
}

// Default permissions by role
export const DEFAULT_PERMISSIONS: Record<UserRole, string[]> = {
  COMPANY: [
    'view_applications',
    'create_applications',
    'upload_documents',
    'view_documents',
    'view_suppliers',
    'manage_suppliers',
    'view_ingredients',
    'manage_ingredients',
  ],
  CONSULTANT: [
    'view_applications',
    'create_applications',
    'upload_documents',
    'view_documents',
    'view_clients',
    'manage_clients',
    'provide_guidance',
  ],
  AUDITOR: [
    'view_applications',
    'review_applications',
    'conduct_audits',
    'create_ncr',
    'approve_applications',
    'reject_applications',
    'schedule_audits',
  ],
  ADMIN: [
    'view_applications',
    'create_applications',
    'manage_users',
    'manage_system',
    'view_payments',
    'issue_certificates',
    'revoke_certificates',
    'view_reports',
    'configure_settings',
  ],
};

// Mock user database (25+ users across all roles)
export const mockUsers: MockUser[] = [
  // COMPANY USERS (10 users)
  {
    id: '1',
    name: 'Kantin Warisan Sdn Bhd',
    email: 'admin@kantinwarisan.com',
    phone: '+60 12-345 6789',
    role: 'COMPANY',
    organization: 'Kantin Warisan Sdn Bhd',
    status: 'ACTIVE',
    registrationDate: '2024-01-15',
    lastLogin: '2025-01-01',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2025-01-01 14:30', action: 'Logged in', details: 'Successful login from 192.168.1.1' },
      { timestamp: '2025-01-01 14:35', action: 'Updated application', details: 'Modified application APP-001' },
      { timestamp: '2024-12-31 10:15', action: 'Uploaded document', details: 'Uploaded halal certificate' },
      { timestamp: '2024-12-30 16:20', action: 'Logged out', details: 'Session ended' },
      { timestamp: '2024-12-30 09:00', action: 'Logged in', details: 'Successful login from 192.168.1.5' },
    ],
    associatedRecords: {
      applications: 3,
      documents: 12,
      payments: 5,
    },
  },
  {
    id: '5',
    name: 'Restoran Al-Barakah',
    email: 'info@albarakah.com',
    phone: '+60 13-456 7890',
    role: 'COMPANY',
    organization: 'Restoran Al-Barakah',
    status: 'ACTIVE',
    registrationDate: '2024-04-05',
    lastLogin: '2024-12-28',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-28 09:15', action: 'Logged in', details: 'Successful login from 192.168.2.10' },
      { timestamp: '2024-12-28 09:30', action: 'Created application', details: 'New halal certification application' },
      { timestamp: '2024-12-27 16:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 2,
      documents: 8,
      payments: 3,
    },
  },
  {
    id: '7',
    name: 'Bakery House Sdn Bhd',
    email: 'contact@bakeryhouse.com',
    phone: '+60 14-567 8901',
    role: 'COMPANY',
    organization: 'Bakery House Sdn Bhd',
    status: 'INACTIVE',
    registrationDate: '2024-06-18',
    lastLogin: '2024-11-15',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-11-15 11:20', action: 'Logged in', details: 'Successful login from 192.168.3.15' },
      { timestamp: '2024-11-15 11:45', action: 'Viewed documents', details: 'Accessed document vault' },
      { timestamp: '2024-11-14 14:30', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 1,
      documents: 5,
      payments: 2,
    },
  },
  {
    id: '9',
    name: 'Food Processing Co',
    email: 'admin@foodprocessing.com',
    phone: '+60 15-678 9012',
    role: 'COMPANY',
    organization: 'Food Processing Co',
    status: 'ACTIVE',
    registrationDate: '2024-08-30',
    lastLogin: '2024-12-27',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-27 13:10', action: 'Logged in', details: 'Successful login from 192.168.4.20' },
      { timestamp: '2024-12-27 13:25', action: 'Added supplier', details: 'Added new halal supplier' },
      { timestamp: '2024-12-26 17:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 4,
      documents: 15,
      payments: 6,
    },
  },
  {
    id: '11',
    name: 'Nasi Lemak Express Sdn Bhd',
    email: 'info@nasilemakexpress.com',
    phone: '+60 16-789 0123',
    role: 'COMPANY',
    organization: 'Nasi Lemak Express Sdn Bhd',
    status: 'ACTIVE',
    registrationDate: '2024-09-12',
    lastLogin: '2024-12-31',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-31 10:00', action: 'Logged in', details: 'Successful login from 192.168.5.25' },
      { timestamp: '2024-12-31 10:15', action: 'Submitted application', details: 'New certification application submitted' },
      { timestamp: '2024-12-30 15:30', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 1,
      documents: 6,
      payments: 1,
    },
  },
  {
    id: '13',
    name: 'Catering Services Ltd',
    email: 'admin@cateringservices.com',
    phone: '+60 17-890 1234',
    role: 'COMPANY',
    organization: 'Catering Services Ltd',
    status: 'ACTIVE',
    registrationDate: '2024-10-05',
    lastLogin: '2024-12-29',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-29 08:45', action: 'Logged in', details: 'Successful login from 192.168.6.30' },
      { timestamp: '2024-12-29 09:00', action: 'Uploaded documents', details: 'Uploaded facility inspection report' },
      { timestamp: '2024-12-28 16:45', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 2,
      documents: 10,
      payments: 3,
    },
  },
  {
    id: '15',
    name: 'Halal Food Industries',
    email: 'contact@halalfood.com',
    phone: '+60 18-901 2345',
    role: 'COMPANY',
    organization: 'Halal Food Industries',
    status: 'ACTIVE',
    registrationDate: '2024-11-01',
    lastLogin: '2024-12-30',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-30 12:00', action: 'Logged in', details: 'Successful login from 192.168.7.35' },
      { timestamp: '2024-12-30 12:20', action: 'Managed ingredients', details: 'Updated ingredient halal status' },
      { timestamp: '2024-12-29 17:15', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 3,
      documents: 14,
      payments: 4,
    },
  },
  {
    id: '17',
    name: 'Restoran Warisan',
    email: 'info@restoranwarisan.com',
    phone: '+60 19-012 3456',
    role: 'COMPANY',
    organization: 'Restoran Warisan',
    status: 'ACTIVE',
    registrationDate: '2024-11-20',
    lastLogin: '2024-12-31',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-31 11:30', action: 'Logged in', details: 'Successful login from 192.168.8.40' },
      { timestamp: '2024-12-31 11:45', action: 'Viewed application status', details: 'Checked certification progress' },
      { timestamp: '2024-12-30 16:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 1,
      documents: 7,
      payments: 2,
    },
  },
  {
    id: '19',
    name: 'Meat Processing Plant',
    email: 'admin@meatprocessing.com',
    phone: '+60 11-123 4567',
    role: 'COMPANY',
    organization: 'Meat Processing Plant',
    status: 'ACTIVE',
    registrationDate: '2024-12-01',
    lastLogin: '2024-12-28',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-28 14:20', action: 'Logged in', details: 'Successful login from 192.168.9.45' },
      { timestamp: '2024-12-28 14:35', action: 'Updated supplier list', details: 'Added halal meat supplier' },
      { timestamp: '2024-12-27 17:30', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 2,
      documents: 9,
      payments: 3,
    },
  },
  {
    id: '21',
    name: 'Seafood Delights Co',
    email: 'contact@seafooddelights.com',
    phone: '+60 12-234 5678',
    role: 'COMPANY',
    organization: 'Seafood Delights Co',
    status: 'SUSPENDED',
    registrationDate: '2024-12-10',
    lastLogin: '2024-12-20',
    permissions: DEFAULT_PERMISSIONS.COMPANY,
    activityLog: [
      { timestamp: '2024-12-20 10:00', action: 'Logged in', details: 'Successful login from 192.168.10.50' },
      { timestamp: '2024-12-20 10:15', action: 'Account suspended', details: 'Suspended due to compliance violation' },
      { timestamp: '2024-12-19 16:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 1,
      documents: 4,
      payments: 1,
    },
  },

  // CONSULTANT USERS (6 users)
  {
    id: '2',
    name: 'Ahmad Hassan',
    email: 'ahmad@consultant.com',
    phone: '+60 13-987 6543',
    role: 'CONSULTANT',
    organization: 'Halal Advisory Services',
    status: 'ACTIVE',
    registrationDate: '2024-02-20',
    lastLogin: '2024-12-30',
    permissions: DEFAULT_PERMISSIONS.CONSULTANT,
    activityLog: [
      { timestamp: '2024-12-30 09:00', action: 'Logged in', details: 'Successful login from 192.168.11.10' },
      { timestamp: '2024-12-30 09:30', action: 'Reviewed client application', details: 'Provided guidance for APP-002' },
      { timestamp: '2024-12-29 17:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 5,
      payments: 0,
      clients: 8,
    },
  },
  {
    id: '6',
    name: 'Siti Nurhaliza',
    email: 'siti@consultant.com',
    phone: '+60 14-876 5432',
    role: 'CONSULTANT',
    organization: 'Halal Consulting Group',
    status: 'ACTIVE',
    registrationDate: '2024-05-12',
    lastLogin: '2024-12-29',
    permissions: DEFAULT_PERMISSIONS.CONSULTANT,
    activityLog: [
      { timestamp: '2024-12-29 11:15', action: 'Logged in', details: 'Successful login from 192.168.12.15' },
      { timestamp: '2024-12-29 11:45', action: 'Uploaded guidance document', details: 'Shared halal compliance checklist with client' },
      { timestamp: '2024-12-28 16:30', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 8,
      payments: 0,
      clients: 12,
    },
  },
  {
    id: '10',
    name: 'Dr. Rahman',
    email: 'rahman@consultant.com',
    phone: '+60 15-765 4321',
    role: 'CONSULTANT',
    organization: 'Islamic Food Consultancy',
    status: 'SUSPENDED',
    registrationDate: '2024-09-14',
    lastLogin: '2024-10-20',
    permissions: DEFAULT_PERMISSIONS.CONSULTANT,
    activityLog: [
      { timestamp: '2024-10-20 14:00', action: 'Logged in', details: 'Successful login from 192.168.13.20' },
      { timestamp: '2024-10-20 14:30', action: 'Account suspended', details: 'Suspended pending investigation' },
      { timestamp: '2024-10-19 17:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 3,
      payments: 0,
      clients: 5,
    },
  },
  {
    id: '12',
    name: 'Fatimah Zahra',
    email: 'fatimah@consultant.com',
    phone: '+60 16-654 3210',
    role: 'CONSULTANT',
    organization: 'Halal Excellence Consulting',
    status: 'ACTIVE',
    registrationDate: '2024-10-08',
    lastLogin: '2024-12-31',
    permissions: DEFAULT_PERMISSIONS.CONSULTANT,
    activityLog: [
      { timestamp: '2024-12-31 08:30', action: 'Logged in', details: 'Successful login from 192.168.14.25' },
      { timestamp: '2024-12-31 09:00', action: 'Scheduled client meeting', details: 'Set up consultation session' },
      { timestamp: '2024-12-30 17:15', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 6,
      payments: 0,
      clients: 10,
    },
  },
  {
    id: '14',
    name: 'Ismail Abdullah',
    email: 'ismail@consultant.com',
    phone: '+60 17-543 2109',
    role: 'CONSULTANT',
    organization: 'Halal Standards Consulting',
    status: 'ACTIVE',
    registrationDate: '2024-11-03',
    lastLogin: '2024-12-30',
    permissions: DEFAULT_PERMISSIONS.CONSULTANT,
    activityLog: [
      { timestamp: '2024-12-30 10:45', action: 'Logged in', details: 'Successful login from 192.168.15.30' },
      { timestamp: '2024-12-30 11:15', action: 'Submitted client report', details: 'Completed compliance assessment report' },
      { timestamp: '2024-12-29 16:45', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 7,
      payments: 0,
      clients: 9,
    },
  },
  {
    id: '16',
    name: 'Nurul Ain',
    email: 'nurul@consultant.com',
    phone: '+60 18-432 1098',
    role: 'CONSULTANT',
    organization: 'Halal Certification Advisors',
    status: 'ACTIVE',
    registrationDate: '2024-11-15',
    lastLogin: '2024-12-29',
    permissions: DEFAULT_PERMISSIONS.CONSULTANT,
    activityLog: [
      { timestamp: '2024-12-29 13:20', action: 'Logged in', details: 'Successful login from 192.168.16.35' },
      { timestamp: '2024-12-29 13:50', action: 'Updated client profile', details: 'Modified client information' },
      { timestamp: '2024-12-28 17:30', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 4,
      payments: 0,
      clients: 7,
    },
  },

  // AUDITOR USERS (6 users)
  {
    id: '3',
    name: 'Ustaz Mohamed Ibrahim',
    email: 'mohamed@jais.gov.my',
    phone: '+60 14-321 0987',
    role: 'AUDITOR',
    organization: 'JAIS',
    status: 'ACTIVE',
    registrationDate: '2024-03-10',
    lastLogin: '2025-01-01',
    permissions: DEFAULT_PERMISSIONS.AUDITOR,
    activityLog: [
      { timestamp: '2025-01-01 08:00', action: 'Logged in', details: 'Successful login from 192.168.17.10' },
      { timestamp: '2025-01-01 08:30', action: 'Reviewed application', details: 'Completed review for APP-003' },
      { timestamp: '2024-12-31 17:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 10,
      payments: 0,
      assignedAudits: 15,
    },
  },
  {
    id: '8',
    name: 'Ustazah Aminah',
    email: 'aminah@jais.gov.my',
    phone: '+60 15-210 9876',
    role: 'AUDITOR',
    organization: 'JAIS',
    status: 'ACTIVE',
    registrationDate: '2024-07-22',
    lastLogin: '2024-12-31',
    permissions: DEFAULT_PERMISSIONS.AUDITOR,
    activityLog: [
      { timestamp: '2024-12-31 09:15', action: 'Logged in', details: 'Successful login from 192.168.18.15' },
      { timestamp: '2024-12-31 09:45', action: 'Conducted audit', details: 'Completed on-site audit for Kantin Warisan' },
      { timestamp: '2024-12-30 17:30', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 8,
      payments: 0,
      assignedAudits: 12,
    },
  },
  {
    id: '18',
    name: 'Ustaz Yusof',
    email: 'yusof@jakim.gov.my',
    phone: '+60 19-109 8765',
    role: 'AUDITOR',
    organization: 'JAKIM',
    status: 'ACTIVE',
    registrationDate: '2024-11-25',
    lastLogin: '2024-12-30',
    permissions: DEFAULT_PERMISSIONS.AUDITOR,
    activityLog: [
      { timestamp: '2024-12-30 08:45', action: 'Logged in', details: 'Successful login from 192.168.19.20' },
      { timestamp: '2024-12-30 09:15', action: 'Scheduled audit', details: 'Scheduled on-site audit for next week' },
      { timestamp: '2024-12-29 17:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 6,
      payments: 0,
      assignedAudits: 10,
    },
  },
  {
    id: '20',
    name: 'Ustazah Khadijah',
    email: 'khadijah@jakim.gov.my',
    phone: '+60 11-098 7654',
    role: 'AUDITOR',
    organization: 'JAKIM',
    status: 'ACTIVE',
    registrationDate: '2024-12-05',
    lastLogin: '2024-12-31',
    permissions: DEFAULT_PERMISSIONS.AUDITOR,
    activityLog: [
      { timestamp: '2024-12-31 10:00', action: 'Logged in', details: 'Successful login from 192.168.20.25' },
      { timestamp: '2024-12-31 10:30', action: 'Created NCR', details: 'Issued Non-Conformance Report for minor finding' },
      { timestamp: '2024-12-30 16:45', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 7,
      payments: 0,
      assignedAudits: 11,
    },
  },
  {
    id: '22',
    name: 'Ustaz Hafiz',
    email: 'hafiz@jais.gov.my',
    phone: '+60 12-987 6543',
    role: 'AUDITOR',
    organization: 'JAIS',
    status: 'ACTIVE',
    registrationDate: '2024-12-15',
    lastLogin: '2024-12-29',
    permissions: DEFAULT_PERMISSIONS.AUDITOR,
    activityLog: [
      { timestamp: '2024-12-29 11:30', action: 'Logged in', details: 'Successful login from 192.168.21.30' },
      { timestamp: '2024-12-29 12:00', action: 'Approved application', details: 'Approved halal certification for Food Processing Co' },
      { timestamp: '2024-12-28 17:15', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 5,
      payments: 0,
      assignedAudits: 8,
    },
  },
  {
    id: '24',
    name: 'Ustazah Zainab',
    email: 'zainab@jakim.gov.my',
    phone: '+60 13-876 5432',
    role: 'AUDITOR',
    organization: 'JAKIM',
    status: 'INACTIVE',
    registrationDate: '2024-12-18',
    lastLogin: '2024-12-22',
    permissions: DEFAULT_PERMISSIONS.AUDITOR,
    activityLog: [
      { timestamp: '2024-12-22 14:00', action: 'Logged in', details: 'Successful login from 192.168.22.35' },
      { timestamp: '2024-12-22 14:30', action: 'Reviewed documents', details: 'Checked uploaded certification documents' },
      { timestamp: '2024-12-21 17:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 4,
      payments: 0,
      assignedAudits: 5,
    },
  },

  // ADMIN USERS (3 users)
  {
    id: '4',
    name: 'Admin User',
    email: 'admin@halmas.gov.my',
    phone: '+60 15-555 5555',
    role: 'ADMIN',
    organization: 'HALMAS',
    status: 'ACTIVE',
    registrationDate: '2024-01-01',
    lastLogin: '2025-01-01',
    permissions: DEFAULT_PERMISSIONS.ADMIN,
    activityLog: [
      { timestamp: '2025-01-01 07:00', action: 'Logged in', details: 'Successful login from 192.168.23.10' },
      { timestamp: '2025-01-01 07:30', action: 'Updated system settings', details: 'Modified application fee amount' },
      { timestamp: '2024-12-31 18:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 20,
      payments: 0,
    },
  },
  {
    id: '23',
    name: 'System Administrator',
    email: 'sysadmin@halmas.gov.my',
    phone: '+60 14-444 4444',
    role: 'ADMIN',
    organization: 'HALMAS',
    status: 'ACTIVE',
    registrationDate: '2024-01-05',
    lastLogin: '2024-12-31',
    permissions: DEFAULT_PERMISSIONS.ADMIN,
    activityLog: [
      { timestamp: '2024-12-31 08:00', action: 'Logged in', details: 'Successful login from 192.168.24.15' },
      { timestamp: '2024-12-31 08:30', action: 'Managed users', details: 'Suspended user account due to violation' },
      { timestamp: '2024-12-30 18:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 15,
      payments: 0,
    },
  },
  {
    id: '25',
    name: 'Support Administrator',
    email: 'support@halmas.gov.my',
    phone: '+60 16-333 3333',
    role: 'ADMIN',
    organization: 'HALMAS',
    status: 'ACTIVE',
    registrationDate: '2024-02-01',
    lastLogin: '2024-12-30',
    permissions: DEFAULT_PERMISSIONS.ADMIN,
    activityLog: [
      { timestamp: '2024-12-30 09:00', action: 'Logged in', details: 'Successful login from 192.168.25.20' },
      { timestamp: '2024-12-30 09:45', action: 'Issued certificate', details: 'Manually issued certificate CERT-20241230-006' },
      { timestamp: '2024-12-29 18:00', action: 'Logged out', details: 'Session ended' },
    ],
    associatedRecords: {
      applications: 0,
      documents: 12,
      payments: 0,
    },
  },
];

/**
 * Get user by ID
 */
export function getUserById(id: string): MockUser | undefined {
  return mockUsers.find((user) => user.id === id);
}

/**
 * Get user by email
 */
export function getUserByEmail(email: string): MockUser | undefined {
  return mockUsers.find((user) => user.email.toLowerCase() === email.toLowerCase());
}

/**
 * Get users by role
 */
export function getUsersByRole(role: UserRole): MockUser[] {
  return mockUsers.filter((user) => user.role === role);
}

/**
 * Get users by status
 */
export function getUsersByStatus(status: UserStatus): MockUser[] {
  return mockUsers.filter((user) => user.status === status);
}

/**
 * Get users by organization
 */
export function getUsersByOrganization(organization: string): MockUser[] {
  return mockUsers.filter((user) => user.organization === organization);
}

/**
 * Search users by name, email, or organization
 */
export function searchUsers(query: string): MockUser[] {
  const lowerQuery = query.toLowerCase();
  return mockUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(lowerQuery) ||
      user.email.toLowerCase().includes(lowerQuery) ||
      user.organization.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get all users
 */
export function getAllUsers(): MockUser[] {
  return mockUsers;
}

/**
 * Get user statistics
 */
export function getUserStatistics() {
  return {
    total: mockUsers.length,
    company: mockUsers.filter((u) => u.role === 'COMPANY').length,
    consultant: mockUsers.filter((u) => u.role === 'CONSULTANT').length,
    auditor: mockUsers.filter((u) => u.role === 'AUDITOR').length,
    admin: mockUsers.filter((u) => u.role === 'ADMIN').length,
    active: mockUsers.filter((u) => u.status === 'ACTIVE').length,
    inactive: mockUsers.filter((u) => u.status === 'INACTIVE').length,
    suspended: mockUsers.filter((u) => u.status === 'SUSPENDED').length,
  };
}
